import React, { useRef, useEffect, useState } from 'react';
import { Link } from '../../../components/link';
import styles from './payment-option-select.module.scss';

import { PageTitle } from 'components/page-title';
import { Language } from '_types/language';
import { useSelector } from 'react-redux';
import { languageSelector } from 'store-modules/app-settings';
import {
    returnCopyForLanguage,
    returnRoutesForLanguage,
} from 'services/language-service';
import DiscountCode from './discount-code';
import { Discount } from '../_types';
// import { StripeProvider } from './stripe';
import { InAppPurchase } from './in-app-purchase/in-app-purchase';

import { SessionResponse } from 'pages/create-premium-page/_services';
import { Product } from 'pages/create-premium-page/_types';
import { routeObj } from 'pages/account-page/account-home/account-home';
import { IS_MOBILE_APP } from './../../../constants';
import subscriptionClient from 'api/subscription-client';

type Props = {
    selectedProduct: Product | undefined;
    session: SessionResponse;
    discount: Discount | undefined;
    discountCode: string | undefined;
    onSuccess: () => void;
    onCancel: () => void;
    onCancelProcess: () => void;
    onCodeSelected: (text: string) => void;
    onCodeCleared: () => void;
};

interface ViewCopy {
    title: string;
    cancelProcessBtn: string;
    changePlan: string;
    linkPrivacy: string;
    linkTnc: string;
}

const nbCopy: ViewCopy = {
    title: 'Fullfør betaling',
    cancelProcessBtn: 'Avbryt betaling',
    changePlan: 'Endre plan',
    linkPrivacy: 'Personvern',
    linkTnc: 'Vilkår for bruk',
};

const enCopy: ViewCopy = {
    title: 'Complete payment',
    cancelProcessBtn: 'Cancel payment',
    changePlan: 'Change plan',
    linkPrivacy: 'Privacy policy',
    linkTnc: 'Terms of use',
};

const copies: { name: Language; copy: ViewCopy }[] = [
    {
        name: 'nb',
        copy: nbCopy,
    },
    {
        name: 'en',
        copy: enCopy,
    },
];

const PaymentOptionSelect: React.FC<Props> = ({
    selectedProduct,
    discount,
    discountCode,
    onSuccess,
    onCancel,
    onCancelProcess,
    onCodeSelected,
    onCodeCleared,
}: Props) => {
    const language: Language = useSelector(languageSelector);
    const ref = useRef<HTMLDivElement>(null);
    const [paymentComplete, setPaymentComplete] = useState(false);

    const beforeOnSuccess = () => {
        setPaymentComplete(true);
        onSuccess();
    };

    useEffect(() => {
        ref?.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    //This checks every 10 seconds if the user has already paid on another device, the user will get redirected to "Welcome"
    useEffect(() => {
        const interval = setInterval(() => {
            if (!paymentComplete)
                subscriptionClient.getActiveSubscription().then((sub) => {
                    const next_payment_date = sub?.data?.subscription
                        .next_payment_date
                        ? new Date(sub?.data?.subscription.next_payment_date)
                        : null;
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    if (next_payment_date) {
                        if (next_payment_date > today) {
                            beforeOnSuccess();
                        }
                    }
                });
        }, 10000);
        return () => clearInterval(interval);
    }, []);

    const routes = returnRoutesForLanguage(language, routeObj);

    const { title, cancelProcessBtn, changePlan, linkPrivacy, linkTnc } =
        returnCopyForLanguage(language, copies);

    const planSummary = selectedProduct ? (
        <section className={styles.PlanSummary} aria-label='plan summary'>
            <div className={styles.PlanSummary__Content}>
                <b>{selectedProduct.name}</b>
                <span>•</span>

                {discount?.discountedPrice ? (
                    <s>
                        {selectedProduct.price}&nbsp;
                        {selectedProduct.currency}
                    </s>
                ) : (
                    <>
                        {selectedProduct.price}&nbsp;
                        {selectedProduct.currency}
                    </>
                )}
                <span>•</span>
                <button
                    type='button'
                    className={styles.ButtonLink}
                    onClick={onCancel}
                >
                    {changePlan}
                </button>
            </div>
        </section>
    ) : null;

    return (
        <article
            data-testid='component-payment-select'
            className={styles.PaymentOptionSelect}
        >
            <PageTitle title={title} />
            {planSummary}
            <section className={styles.Content}>
                <section
                    className={styles.CCForm}
                    aria-labelledby='form'
                    data-testid='section-form'
                >
                    {!IS_MOBILE_APP && (
                        <DiscountCode
                            language={language}
                            onCodeSelected={onCodeSelected}
                            onCodeCleared={onCodeCleared}
                            discount={discount}
                            currency={selectedProduct?.currency}
                        />
                    )}
                    {IS_MOBILE_APP && selectedProduct && (
                        <InAppPurchase
                            discountCode={discountCode}
                            productID={selectedProduct?.id}
                            productPrice={selectedProduct.price}
                            onSuccess={beforeOnSuccess}
                            onCancel={onCancel}
                        />
                    )}
                    {/* {!IS_MOBILE_APP && selectedProduct && (
                        <StripeProvider
                            price={selectedProduct?.price}
                            currency={selectedProduct?.currency}
                            discountCode={discountCode}
                            productID={selectedProduct?.id}
                            onSuccess={beforeOnSuccess}
                            onCancel={onCancel}
                        />
                    )} */}
                    <div className={styles.CancelProcess}>
                        <button
                            type='button'
                            className={styles.ButtonLink}
                            onClick={onCancelProcess}
                        >
                            {cancelProcessBtn}
                        </button>
                    </div>
                </section>
                <section className={styles.links} aria-label='links'>
                    <Link className={styles.link} to={routes.privacyTerms}>
                        {linkPrivacy}
                    </Link>
                    <Link className={styles.link} to={routes.tnc}>
                        {linkTnc}
                    </Link>
                </section>
            </section>

            <div ref={ref} />
        </article>
    );
};

export default React.memo(PaymentOptionSelect);
